import { Pencil, Trash2 } from "lucide-react";
import { accountLabel, TRANSFER_KIND_LABELS, TX_TYPE_LABELS, type Transaction } from "@/lib/finance";
import { TAG_STYLES, typeLabel, typeTag } from "@/lib/budget";
import { formatDate, money, moneyIn, rateText, signedMoney } from "@/lib/format";

const amountColor = (t: Transaction) => (t.flow === "in" ? "text-[#1B6E4F]" : t.flow === "out" ? "text-[#B23A1E]" : "text-slate-600");

interface Props { items: Transaction[]; onEdit: (t: Transaction) => void; onDelete: (t: Transaction) => void }

function Chip({ t }: { t: Transaction }) {
  const kind = t.type === "transfer" && t.transfer_kind ? TRANSFER_KIND_LABELS[t.transfer_kind] + (t.is_savings_withdrawal ? " · Retiro de ahorro" : "") : null;
  const label = t.is_investment_return ? "Rendimiento" : kind ?? t.category_name ?? (t.type === "income" || t.type === "expense" ? "Sin categoría" : TX_TYPE_LABELS[t.type]);
  const tag = t.type === "expense" || t.is_loan_payment ? typeTag(t.group) : null;
  return (
    <span className="flex items-center gap-1">
      {tag && <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-bold ${TAG_STYLES[tag]}`} title={typeLabel(t.group)} data-testid={`tx-type-tag-${t.id}`}>{tag}</span>}
      <span className="inline-block max-w-full truncate rounded-full border border-[#DDE1E8] px-2.5 py-0.5 text-xs text-[#1B2A4A]" data-testid={`tx-chip-${t.id}`}>{label}</span>
      {t.msi_total ? <span className="rounded-full bg-[#FBF3DF] px-2 py-0.5 text-xs font-semibold text-[#8A6A1F]" data-testid={`tx-msi-badge-${t.id}`}>{t.msi_total} MSI</span> : null}
    </span>
  );
}

function MsiDetail({ t }: { t: Transaction }) {
  const s = t.msi_schedule ?? [];
  if (!t.msi_total || !s.length) return null;
  return (
    <p className="text-xs text-slate-500" data-testid={`tx-msi-detail-${t.id}`}>
      Mensualidad 1 de {t.msi_total} ({money(s[0].amount)}) · siguientes: {s.slice(1).map((x) => formatDate(x.date)).join(", ")}
    </p>
  );
}

function FxNote({ t }: { t: Transaction }) {
  const parts: string[] = [];
  if (t.source_native != null && t.source_currency && t.source_currency !== "MXN") parts.push(moneyIn(t.source_native, t.source_currency));
  if (t.dest_native != null && t.destination_currency && t.destination_currency !== "MXN" && !(t.type === "transfer" && t.source_native != null && t.source_currency === t.destination_currency)) parts.push(moneyIn(t.dest_native, t.destination_currency));
  if (t.original_amount && t.original_currency) parts.push(`Original ${moneyIn(t.original_amount, t.original_currency)}`);
  if (!parts.length) return null;
  return <span className="block text-[11px] font-normal text-slate-500" data-testid={`tx-fx-note-${t.id}`}>{parts.join(" → ")}{t.fx_rate ? ` · TC ${rateText(t.fx_rate)}` : ""}</span>;
}

function RowActions({ t, onEdit, onDelete }: { t: Transaction } & Omit<Props, "items">) {
  return (
    <div className="flex justify-end gap-1">
      <button onClick={() => onEdit(t)} className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-[#1B2A4A]" aria-label="Editar" data-testid={`tx-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={() => onDelete(t)} className="rounded p-1.5 text-slate-400 hover:bg-[#F8ECE8] hover:text-[#B23A1E]" aria-label="Borrar" data-testid={`tx-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}

export function MovementsList({ items, onEdit, onDelete }: Props) {
  if (items.length === 0) {
    return <p className="py-14 text-center text-sm text-slate-500" data-testid="movements-empty">No hay movimientos con estos filtros.</p>;
  }
  return (
    <>
      <div className="mt-5 hidden md:block" data-testid="movements-table">
        <div className="grid grid-cols-[92px_1.6fr_1fr_1.2fr_130px_72px] gap-3 rounded-lg bg-[#F4F5F7] px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
          <span>Fecha</span><span>Descripción</span><span>Categoría</span><span>Cuenta</span><span className="text-right">Monto</span><span />
        </div>
        <ul className="divide-y divide-[#DDE1E8]">
          {items.map((t) => (
            <li key={t.id} className="grid grid-cols-[92px_1.6fr_1fr_1.2fr_130px_72px] items-center gap-3 px-4 py-3.5" data-testid={`tx-row-${t.id}`}>
              <span className="tabular text-sm text-slate-500">{formatDate(t.date)}</span>
              <div className="min-w-0">
                <p className="truncate font-semibold text-[#1B2A4A]">{t.description}</p>
                <MsiDetail t={t} />
                {t.notes && <p className="truncate text-xs text-slate-400">{t.notes}</p>}
              </div>
              <div className="min-w-0"><Chip t={t} /></div>
              <span className="truncate text-sm text-slate-600">{accountLabel(t)}</span>
              <span className={`tabular text-right font-semibold ${amountColor(t)}`} data-testid={`tx-amount-${t.id}`}>{signedMoney(t.amount, t.flow)}<FxNote t={t} /></span>
              <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
            </li>
          ))}
        </ul>
      </div>
      <ul className="mt-4 space-y-3 md:hidden" data-testid="movements-cards">
        {items.map((t) => (
          <li key={t.id} className="rounded-xl border border-[#DDE1E8] p-4" data-testid={`tx-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-slate-500">{formatDate(t.date)} · {accountLabel(t)}</p>
                <p className="mt-0.5 truncate font-semibold text-[#1B2A4A]">{t.description}</p>
                <MsiDetail t={t} />
              </div>
              <span className={`tabular shrink-0 text-right font-semibold ${amountColor(t)}`}>{signedMoney(t.amount, t.flow)}<FxNote t={t} /></span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2">
              <Chip t={t} />
              <RowActions t={t} onEdit={onEdit} onDelete={onDelete} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}
